// Product group row filter for the by-warehouse stock matrix, plus the
// free-text search that already lived on the page. Pure functions so the
// selection logic is unit-tested without rendering the popovers.

// Sentinel option key for rows with no product group. Same idea as the
// "Unassigned" group in groupProducts, so the two features read alike.
export const UNASSIGNED = "__unassigned__";

export interface FilterOption {
  key: string;
  label: string;
  // How deep the option sits in a tree; the list indents by it. 0 = top level.
  depth?: number;
}

export interface FilterableRow {
  external_id: string;
  name: string;
  product_line_full_name?: string | null;
  product_line_short_name?: string | null;
  product_node_path?: string | null;
  // Group ids from the top of the tree down to the row's own group; empty when
  // the product has no group.
  product_node_lineage_ids?: string[];
}

export interface ProductFilters {
  query: string;
  // Group ids (or UNASSIGNED) to keep. Empty set = no filter (every row passes).
  groups: Set<string>;
}

// The groups in the order given (tree order from the server), plus an
// "Unassigned" entry last when some row has no group.
export function groupOptions(
  groups: { id: string; path: string; depth: number }[],
  rows: FilterableRow[],
): (FilterOption & { depth: number })[] {
  const options = groups.map((g) => ({
    key: g.id,
    // The path already holds the parents ("Halio › Oral Care"); the list shows
    // only the group's own name and indents it by depth.
    label: g.path.split(" › ").pop() ?? g.path,
    depth: g.depth,
  }));
  const hasUnassigned = rows.some((r) => !r.product_node_lineage_ids?.length);
  return hasUnassigned ? [...options, { key: UNASSIGNED, label: "Unassigned", depth: 0 }] : options;
}

function matchesQuery(row: FilterableRow, q: string): boolean {
  return [
    row.name,
    row.external_id,
    row.product_line_full_name,
    row.product_line_short_name,
    row.product_node_path,
  ].some((field) => field?.toLowerCase().includes(q));
}

function inGroup(row: FilterableRow, groupId: string): boolean {
  if (groupId === UNASSIGNED) return !row.product_node_lineage_ids?.length;
  return row.product_node_lineage_ids?.includes(groupId) ?? false;
}

// AND of search and group selection. Returns the input array untouched when
// nothing is active so memoised consumers keep identity.
export function filterProducts<T extends FilterableRow>(
  rows: T[],
  { query, groups }: ProductFilters,
): T[] {
  const q = query.trim().toLowerCase();
  if (!q && groups.size === 0) return rows;

  return rows.filter(
    (row) =>
      (groups.size === 0 || [...groups].some((g) => inGroup(row, g))) &&
      (!q || matchesQuery(row, q)),
  );
}
